import { TimeStampController } from '@streem/sdk-react';
import { styled } from '@streem/ui-react';
import { useObserver } from 'mobx-react';
import { FC } from 'react';
import appLogger from '../../util/logging/app_logger';
import { TimeStampControllerProvider } from '../../hooks/use_timestamp_controller';
import { MediaGallerySelected } from './media_gallery_selected';
import { MAX_VIDEO_HEIGHT, MAX_VIDEO_WIDTH } from './shared.styles';
import { CallRecording } from './call_recording';
import { Bookmarks } from './bookmarks';
import { Streemshots } from './streemshots';
import { useMediaGallery } from '../../hooks/use_media_gallery';
import { Transcriptions } from './transcriptions';
import { TranscriptionContextProvider } from '../../hooks/use_transcription_context';

const log = appLogger.extend('Media Gallery');

/**
 * Responsive mosaic grid of recordings and streemshots
 */
export const MediaGallery: FC<
    React.PropsWithChildren<{
        readOnly: boolean;
        videoPreviewEnabled?: boolean;
    }>
> = ({ readOnly, videoPreviewEnabled }) => {
    const {
        detailSession,
        streemshots,
        selectedStreemshotIds,
        recordings,
        selectedRecordingIds,
        streemshotsImageDataRef,
        selectionSize,
        isMobile,
        selectionVisible,
        handleArtifactSelect,
        clearSelection,
        errorMessage,
        bookmarks,
        setErrorMessage,
        setSelectedStreemshotIds,
        transcriptionUiEnabled,
        initialTranscriptionContext,
    } = useMediaGallery(log);

    const transcriptions = transcriptionUiEnabled ? (
        <Transcriptions detailSession={detailSession} />
    ) : null;

    return useObserver(() => {
        return (
            <MediaGalleryContainer data-testid="media-gallery">
                <TranscriptionContextProvider value={initialTranscriptionContext}>
                    <TimeStampControllerProvider value={new TimeStampController(recordings)}>
                        <CallRecording
                            recordings={recordings}
                            selectionVisible={selectionVisible}
                            handleArtifactSelect={handleArtifactSelect}
                            detailSession={detailSession}
                            selectedRecordingIds={selectedRecordingIds}
                            previewEnabled={videoPreviewEnabled}
                        />
                        {transcriptions}
                        <Bookmarks
                            readOnly={readOnly}
                            recordings={recordings}
                            detailSession={detailSession}
                            bookmarks={bookmarks}
                        />
                        <Streemshots
                            recordings={recordings}
                            streemshots={streemshots}
                            streemshotsImageDataRef={streemshotsImageDataRef}
                            selectedStreemshotIds={selectedStreemshotIds}
                            handleArtifactSelect={handleArtifactSelect}
                            selectionVisible={selectionVisible}
                            log={log}
                            detailSession={detailSession}
                        />
                    </TimeStampControllerProvider>
                    <MediaGallerySelected
                        detailSession={detailSession}
                        streemshots={streemshots}
                        selectedStreemshotIds={selectedStreemshotIds}
                        recordings={recordings}
                        selectedRecordingIds={selectedRecordingIds}
                        streemshotsImageDataRef={streemshotsImageDataRef}
                        selectionSize={selectionSize}
                        isMobile={isMobile}
                        selectionVisible={selectionVisible}
                        readOnly={readOnly}
                        onUnselect={() => {
                            setErrorMessage('');
                            clearSelection();
                        }}
                        onSelectAll={() => {
                            setErrorMessage('');
                            setSelectedStreemshotIds([new Set(streemshots.map(ss => ss.id))]);
                        }}
                        clearSelection={clearSelection}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        logger={log}
                    />
                </TranscriptionContextProvider>
            </MediaGalleryContainer>
        );
    });
};

const MediaGalleryContainer = styled('section')({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `minmax(15.75rem,${MAX_VIDEO_WIDTH}) minMax(18.4rem, 1fr)`,
    gridTemplateRows: `minmax(10rem, ${MAX_VIDEO_HEIGHT}) repeat(1fr)`,
    columnGap: '1.5rem',
    rowGap: '2.5rem',
    alignContent: 'start',
    '@media(max-width: 1075px)': {
        gridTemplateColumns: '1fr',
    },
});
